import { getFirebaseAuth, getFirebaseUser } from "@mgdx-libs/firebase";
import { onAuthStateChanged, type User } from "firebase/auth";
import { useEffect, useMemo, useState } from "react";

import { OIDCProvider } from "./OIDCProvider";

type UseIsOIDCCredential = (args: { providerId: OIDCProvider }) => boolean;

const hasOIDCCredential = (firebaseUser: User | null, providerId: OIDCProvider) =>
  !!firebaseUser?.providerData.some((data) => data?.providerId === providerId);

export const useIsOIDCCredential: UseIsOIDCCredential = ({ providerId }) => {
  const firebaseUser = getFirebaseUser();

  return useMemo(() => hasOIDCCredential(firebaseUser, providerId), [firebaseUser, providerId]);
};

type UseOIDCCredentialState = (args: { providerId: OIDCProvider }) => {
  hasCredential: boolean;
  isFirebaseAuthReady: boolean;
};

export const useOIDCCredentialState: UseOIDCCredentialState = ({ providerId }) => {
  const [firebaseAuthState, setFirebaseAuthState] = useState(() => {
    const firebaseUser = getFirebaseUser();
    return {
      firebaseUser,
      isFirebaseAuthReady: !!firebaseUser,
    };
  });

  useEffect(() => {
    return onAuthStateChanged(getFirebaseAuth(), (firebaseUser) => {
      setFirebaseAuthState({
        firebaseUser,
        isFirebaseAuthReady: true,
      });
    });
  }, []);

  return useMemo(
    () => ({
      hasCredential: hasOIDCCredential(firebaseAuthState.firebaseUser, providerId),
      isFirebaseAuthReady: firebaseAuthState.isFirebaseAuthReady,
    }),
    [firebaseAuthState, providerId]
  );
};
